import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { $ } from '../lib/api';
import {
  PageHeader, StatsRow, StatCard, TableWrap, TableFooter,
  Pill, Btn, Avatar,
} from '../components/ui';
import { useState } from 'react';
import { useAuthStore } from '../store/auth';
import { MeetingAgendaModal } from '../components/MeetingAgendaModal';

const ROLE_LABEL: Record<string, string> = {
  admin: '管理员', director: '销售总监', manager: '销售主管', sales: '销售员',
};

const ROLE_TONE: Record<string, 'primary' | 'success' | 'warning' | 'default'> = {
  admin: 'primary', director: 'primary', manager: 'success', sales: 'default',
};

export function TeamPage() {
  const nav = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [page, setPage] = useState(1);
  const pageSize = 20;
  // P2-12: 主管开会按钮
  const [showAgenda, setShowAgenda] = useState(false);
  const canHostMeeting = user && ['manager', 'director', 'admin'].includes(user.role);
  const canManageUsers = user && ['admin', 'director'].includes(user.role);
  // manager 的 teamId 直接来自 JWT；admin/director 默认开第一个团队（实际应该让选）
  const meetingTeamId = user?.teamId ?? 1;

  // v1.2-rc2 #3：manager 只看本团队（参考 sales-targets.service.ts 用 teamId 过滤）
  // 后端 GET /users 支持 ?teamId=N（users.service.ts:116 `qb.andWhere('u.teamId = :teamId')`）
  // admin / director 跨团队不过滤；manager 必带自己的 teamId
  const teamFilter: Record<string, any> = (user?.role === 'manager' && user?.teamId)
    ? { teamId: user.teamId } : {};
  const { data, isLoading } = useQuery({
    queryKey: ['team-users', page, pageSize, user?.role, user?.teamId],
    queryFn: () => $.get<{ items: any[]; total: number }>('/users', { page, pageSize, ...teamFilter }),
  });

  const items = (data?.items ?? []) as any[];
  const total = data?.total ?? 0;
  // Pull customer totals for the team
  // 追加B：夹底 0——防历史漂移负值（柏孝阳 -1）拉低团队持客总量（后端已重算+夹底，前端再保险一层）
  const totalCustomers = items.reduce((s, u) => s + Math.max(0, Number(u.currentCustomerCount ?? 0)), 0);
  const totalCapacity = items.reduce((s, u) => s + Number(u.customerCapacity ?? 0), 0);
  const salesCount = items.filter((u) => u.role === 'sales').length;
  const probationCount = items.filter((u) => u.isProbation).length;

  return (
    <>
      <PageHeader
        breadcrumb={['资源', '团队']}
        title="团队"
        subtitle="销售团队成员与产能概览"
        actions={
          <>
            {canHostMeeting && (
              <Btn kind="default" onClick={() => setShowAgenda(true)}>📅 生成本周会议议程</Btn>
            )}
            {canManageUsers && (
              <Btn kind="primary" onClick={() => nav('/admin/users')}>用户管理</Btn>
            )}
          </>
        }
      />
      <div className="work-content">
        <StatsRow>
          <StatCard label="团队人数" value={total} unit="人" />
          <StatCard label="销售员" value={salesCount} unit="人" valueColor="success" />
          <StatCard label="持客总量" value={totalCustomers} unit="个" meta={`容量 ${totalCapacity}`} />
          <StatCard label="试用期" value={probationCount} unit="人" valueColor="warning" />
        </StatsRow>

        <TableWrap>
          <table className="ck-table">
            <thead>
              <tr>
                <th style={{ width: '26%' }}>成员</th>
                <th style={{ width: '12%' }}>角色</th>
                <th style={{ width: '16%' }}>电话</th>
                <th style={{ width: '20%' }}>持客容量</th>
                <th style={{ width: '12%' }}>状态</th>
                <th style={{ width: '14%', textAlign: 'right' }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>加载中…</td></tr>
              )}
              {!isLoading && items.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>暂无成员</td></tr>
              )}
              {items.map((u) => {
                const cap = Number(u.customerCapacity ?? 0);
                const cur = Math.max(0, Number(u.currentCustomerCount ?? 0)); // 追加B：夹底 0
                const pct = cap > 0 ? Math.round((cur / cap) * 100) : 0;
                return (
                  <tr key={u.id}>
                    <td>
                      <div className="cust-cell">
                        <Avatar userId={u.id} src={u.avatarUrl} name={u.realName ?? u.username} size="md" />
                        <div className="cust-info">
                          <div className="cust-name">{u.realName ?? u.username}</div>
                          <div className="cust-meta">@{u.username}</div>
                        </div>
                      </div>
                    </td>
                    <td><Pill tone={ROLE_TONE[u.role] ?? 'default'}>{ROLE_LABEL[u.role] ?? u.role}</Pill></td>
                    <td><span className="time-text">{u.phone ?? '—'}</span></td>
                    <td>
                      {cap === 0
                        ? <span className="time-text muted">— 不持客</span>
                        : (
                          <div className="tbl-progress">
                            <div className="tbl-progress-bar">
                              <div className="tbl-progress-fill" style={{
                                width: `${Math.min(100, pct)}%`,
                                background: pct >= 80 ? 'var(--warning)' : 'var(--success)',
                              }} />
                            </div>
                            <span className="tbl-progress-val">{cur}/{cap}</span>
                          </div>
                        )}
                    </td>
                    <td>
                      {!u.isActive ? <Pill tone="default">已停用</Pill>
                        : u.isProbation ? <Pill tone="warning">试用期</Pill>
                        : <Pill tone="success">在岗</Pill>}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div className="row-actions">
                        <button
                          className="row-action"
                          onClick={() => nav(`/customers?owner=${u.id}`)}
                          data-tip="查看该成员的客户"
                        >
                          查看客户
                        </button>
                        {canManageUsers && (
                          <button
                            className="row-action"
                            onClick={() => nav('/admin/users')}
                            data-tip="到用户管理修改资料"
                          >
                            编辑
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {total > 0 && <TableFooter total={total} page={page} pageSize={pageSize} onPage={setPage} />}
        </TableWrap>
      </div>
      {canHostMeeting && (
        <MeetingAgendaModal
          open={showAgenda}
          teamId={Number(meetingTeamId)}
          teamName={user?.role === 'manager' ? '我的团队' : '团队'}
          onClose={() => setShowAgenda(false)}
        />
      )}
    </>
  );
}
